import { useEffect, useState, useRef, useMemo } from 'react'
import HeroGraph from './HeroGraph'
import Button from '../../ui-components/Button'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import {
  ELiteMembershipStatus,
  MutualFundDetailSections,
  PowerPerformanceForms,
  PowerPerformanceRankDataType,
  PowerPerformanceRankPoint,
  PowerPerformanceRiveStateType,
  PowerPerformanceRiveStates,
  UserInvestmentType,
  WaterfallSections,
  WaterfallValues
} from '../../../types/mutual-fund-detail.types'
import Tabs from '../../ui-components/Tabs'
import MFHeroSectionSkeleton from '../../skeletons/MFHeroSectionSkeleton'
import PowerForm from './PowerForm'
import { HeroSectionUnavailable } from '../../error-states/HeroSectionUnavailable'
import useAlternateISIN from '../../../hooks/useAlternateISIN'
import { lightImpactVibrate, postCTA, trackEvent } from '../../../utils/appBridge'
import { EventNames } from '../../../types/misc.types'
import { useMutualFundDetailScrollEvent } from '../../../hooks/useMutualFundDetailScrollEvent'
import HelpButton from '../../ui-components/HelpButton'
import VaulDrawer from '../../BottomSheetComponents/VaulDrawer'
import { PerformanceBottomSheet } from '../../BottomSheetComponents/PerformanceBottomSheet'
import RenderHTML from '../../ui-components/RenderHTML'
import { MUTUAL_FUND_DETAIL_IMAGE_URL } from '../../../constants'
import Remark from '../../ui-components/Remark'
import { HeroSectionLocked } from './HeroSectionLocked'

const NO_OF_POINTS_6M = 7
const NO_OF_POINTS_1Y = 13
const NO_OF_POINTS_2Y = 25

const POWER_PERFORMANCE_SCREEN_NAME = 'MF_L2_PowerPerformance'

export default function HeroSection({ sectionKey }: { sectionKey: `${MutualFundDetailSections}` }) {
  const {
    data: metadata,
    isin,
    handlePowerPerformanceRiveState,
    isUserElite,
    handleIsUserElite,
    handlePowerForm,
    powerPerformanceRankData: data,
    isPowerPerformanceRankLoading: isLoading,
    isPowerPerformanceRankSuccess: isSuccess,
    isPowerPerformanceRankError: isDataError
  } = useMetaDataContext()
  const [rankData, setRankData] = useState<PowerPerformanceRankDataType['performanceRankList']>([])
  const [isEliteButNoRanks, setIsEliteButNoRanks] = useState(false)
  const [selectedTabId, setSelectedTabId] = useState<string>('6M')
  const [filteredData, setFilteredData] = useState<PowerPerformanceRankDataType['performanceRankList']>([])
  const [userInvestmentType, setUserInvsetmentType] = useState<`${UserInvestmentType}`>(UserInvestmentType.NONE)
  const [isFirstTimeElite, setIsFirstTimeElite] = useState<boolean>(false)
  const [isPowerRankAvailable, setIsPowerRankAvailable] = useState<boolean>(false)

  const [showLabels, setShowLabels] = useState(false)
  const [openVaulBottomSheet, setOpenVaulBottomSheet] = useState(false)
  const performanceHistoryRef = useRef<HTMLDivElement>(null)

  const isInteractionEventLocked = useRef<boolean>(false)

  const { config } = useAlternateISIN(WaterfallSections.HERO)

  const sectionRef = useMutualFundDetailScrollEvent({ sectionKey })

  const powerFormStateHandler = (riveState: PowerPerformanceRiveStateType) => {
    if (config === WaterfallValues.NO) handlePowerPerformanceRiveState(PowerPerformanceRiveStates.UNRATED)
    else handlePowerPerformanceRiveState(riveState)
  }

  useEffect(() => {
    if (data?.data) {
      const dataCopy: PowerPerformanceRankDataType = data?.data
      let rankList: PowerPerformanceRankDataType['performanceRankList'] = dataCopy?.performanceRankList ?? []
      let sortedByMonth = rankList?.sort((a, b) => (b?.x_value ?? 0) - (a?.x_value ?? 0))
      if (rankList?.length < 25 && rankList?.length > 0) {
        let lastPointMonthDate = new Date(sortedByMonth?.slice(-1)?.[0]?.x_value)
        let remainingLength = 25 - sortedByMonth?.length
        while (remainingLength > 0) {
          const lastPointMonthDateCopy = new Date(JSON.parse(JSON.stringify(lastPointMonthDate))) as Date
          const prevMonth = new Date(lastPointMonthDateCopy?.setMonth(lastPointMonthDateCopy?.getMonth() - 1))
          const prevMonthTime = prevMonth?.getTime()
          const prevMothPoint = {
            x_value: prevMonthTime,
            y_value: PowerPerformanceForms.ON_TRACK,
            label: -1
          } as PowerPerformanceRankPoint
          sortedByMonth?.push(prevMothPoint)
          lastPointMonthDate = new Date(prevMonthTime)
          remainingLength--
        }
      }
      rankList = sortedByMonth?.sort((a, b) => (a?.x_value ?? 0) - (b?.x_value ?? 0))
      setRankData(() => rankList)
      const isUserEliteCopy = dataCopy?.eliteMemberShipStatus === ELiteMembershipStatus.ELITE
      powerFormStateHandler(isUserEliteCopy ? PowerPerformanceRiveStates.ELITE : PowerPerformanceRiveStates.NON_ELITE)
      setIsEliteButNoRanks(() => isUserEliteCopy && rankList?.length > 0 && rankList?.slice(-1)?.[0]?.label === null)
      setFilteredData(() => rankList?.slice(-7))
      handleIsUserElite(isUserEliteCopy)
      setUserInvsetmentType(() => dataCopy?.userInvestmentType ?? UserInvestmentType.NONE)
      setIsFirstTimeElite(() => dataCopy?.isFirstTimeElite || false)
      setIsPowerRankAvailable(() => dataCopy?.isPowerRankAvailable || false)
    }
  }, [data])

  useEffect(() => {
    const sliceCount =
      {
        '6M': NO_OF_POINTS_6M,
        '1Y': NO_OF_POINTS_1Y,
        '2Y': NO_OF_POINTS_2Y
      }[selectedTabId] ?? 0
    if (!selectedTabId || rankData?.length === 0) return
    setFilteredData(rankData.slice(-sliceCount))
  }, [selectedTabId, rankData])

  const getYValue = (val: `${PowerPerformanceForms}`) => {
    return (
      {
        [PowerPerformanceForms.IN_FORM]: 3.4,
        [PowerPerformanceForms.ON_TRACK]: 2.5,
        [PowerPerformanceForms.OFF_TRACK]: 1.5,
        [PowerPerformanceForms.OUT_OF_FORM]: 0.5,
        [PowerPerformanceForms.NOT_RATED]: 0.25,
        [PowerPerformanceForms.PERFORMANCE_LOCKED]: 0.25
      }[val] ?? 0.5
    )
  }

  const convertData = (dataList: PowerPerformanceRankDataType['performanceRankList']) => {
    const sampleData: {
      month: number
      value: number
      label: string
      form: `${PowerPerformanceForms}`
      isVisible: boolean
    }[] = []
    dataList.forEach((data) => {
      sampleData.push({
        month: data.x_value,
        value: getYValue(data.y_value),
        label: `${data.label}`,
        form: data.y_value,
        isVisible: data?.label !== -1
      })
    })
    return sampleData
  }

  const tabsData = [
    { id: '6M', label: '6M' },
    { id: '1Y', label: '1Y' },
    { id: '2Y', label: '2Y' }
  ].filter((_tab, index) => index < rankData?.length / 7)

  const powerForm = useMemo(() => {
    const lastDatum = filteredData?.slice(-1)?.[0]
    return {
      form: lastDatum?.y_value,
      time: lastDatum?.x_value
    }
  }, [filteredData])

  useEffect(() => {
    if (powerForm?.form) handlePowerForm(powerForm?.form)
  }, [powerForm])

  const isDataEmpty = () => {
    if (!isSuccess) return false
    const performanceList = (data?.data as PowerPerformanceRankDataType)?.performanceRankList
    return !(performanceList && performanceList?.length > 0)
  }

  const isError = useMemo(() => {
    return isDataError || isDataEmpty()
  }, [isDataError, filteredData, isSuccess])

  useEffect(() => {
    if (isDataEmpty()) powerFormStateHandler(PowerPerformanceRiveStates.UNRATED)
  }, [filteredData, isSuccess])

  useEffect(() => {
    if (isDataError) powerFormStateHandler(`${PowerPerformanceRiveStates.UNABLE_TO_LOAD}`)
  }, [isDataError])

  const isHeroSectionNotAvailable = useMemo(() => {
    const notAvailableCase = config === WaterfallValues.NO || !config
    return notAvailableCase
  }, [config])

  useEffect(() => {
    if (isHeroSectionNotAvailable) powerFormStateHandler(`${PowerPerformanceRiveStates.UNRATED}`)
  }, [isHeroSectionNotAvailable])

  useEffect(() => {
    if (isFirstTimeElite || isUserElite) {
      let showLabelsCopy = true
      if (isEliteButNoRanks) {
        showLabelsCopy = false
        powerFormStateHandler(`${PowerPerformanceRiveStates.UNRATED}`)
      }
      setShowLabels(() => showLabelsCopy)
    }
  }, [isFirstTimeElite, isUserElite, isEliteButNoRanks])

  const handleLabelsButtonClick = () => {
    if (isUserElite) {
      trackEvent({
        eventName: EventNames.UserClicked,
        payload: {
          eventId: 'Power Rank',
          ScreenName: POWER_PERFORMANCE_SCREEN_NAME,
          fundName: isin,
          screenType: 'MF_L2',
          responseName: 'toggle'
        }
      })
      setShowLabels((showLabelsPrev) => {
        return !showLabelsPrev
      })
    } else if (metadata?.eliteIconCta) {
      trackEvent({
        eventName: EventNames.UserClicked,
        payload: {
          ScreenName: POWER_PERFORMANCE_SCREEN_NAME,
          screenType: 'MF_L2',
          eventId: 'Power Rank',
          responseName: 'elite'
        }
      })
      postCTA(JSON.stringify(metadata?.eliteIconCta))
    }
  }

  if (isHeroSectionNotAvailable) return null

  if (isLoading)
    return (
      <div className='h-max w-full'>
        <MFHeroSectionSkeleton />
      </div>
    )

  if (isError)
    return (
      <div ref={sectionRef} className='border-t border-[#EBF2F2] flex flex-col bg-white'>
        {config === WaterfallValues.YES_DIRECT && (
          <div className='flex flex-col pt-5 pb-1 px-5'>
            <Remark lineHeight={19} remarkClassName='py-3'>
              Showing Performance for <span className='font-[500]'> Direct Growth Plan </span>
            </Remark>
          </div>
        )}

        <HeroSectionUnavailable />
      </div>
    )

  if (powerForm?.form === PowerPerformanceForms.PERFORMANCE_LOCKED) {
    return (
      <div ref={sectionRef} className='border-t border-[#EBF2F2] flex flex-col bg-white'>
        {config === WaterfallValues.YES_DIRECT && (
          <div className='flex flex-col pt-5 pb-1 px-5'>
            <Remark lineHeight={19} remarkClassName='py-3'>
              Showing Performance for <span className='font-[500]'> Direct Growth Plan </span>
            </Remark>
          </div>
        )}
        <HeroSectionLocked />
      </div>
    )
  }

  return (
    <>
      <div
        ref={sectionRef}
        className='flex flex-col w-full pb-[24px] bg-white animate-fade-150 border-t border-[#EBF2F2]'
      >
        {config === WaterfallValues.YES_DIRECT && (
          <div className='flex flex-col pt-5 pb-1 px-5'>
            <Remark lineHeight={19} remarkClassName='py-3'>
              Showing Performance for <span className='font-[500]'> Direct Growth Plan </span>
            </Remark>
          </div>
        )}
        <div className='flex flex-col px-5 w-full'>
          <div className={`flex justify-between pb-6 ${config === WaterfallValues.YES_DIRECT ? 'pt-5' : 'pt-6'}`}>
            <div>
              <PowerForm
                powerForm={powerForm?.form}
                time={powerForm?.time}
                userInvestmentType={userInvestmentType}
                isPowerRankAvailable={isPowerRankAvailable}
              />
            </div>

            <div>
              <HelpButton
                aria-label='Help about fund performance'
                onClick={() => setOpenVaulBottomSheet((open) => !open)}
              />
            </div>
          </div>

          <div className='flex flex-col'>
            <h2
              ref={performanceHistoryRef}
              className='text-[#6A6E6E] pb-2 pl-1 text-13 font-[400] m-0'
            >
              Past Trends
            </h2>

            <div>
              {filteredData?.length > 0 && (
                <HeroGraph
                  data={convertData(filteredData)}
                  showLabels={showLabels}
                  graphForm={powerForm?.form}
                  periodLabel={selectedTabId}
                  onInteraction={(_rank: string | number, date: any) => {
                    const isDateToday = date === 'Today'
                    if (!isInteractionEventLocked?.current && !isDateToday) {
                      trackEvent({
                        eventName: EventNames.GraphInteraction,
                        payload: {
                          eventId: 'slider_move',
                          responseName: `${selectedTabId}`,
                          ScreenName: POWER_PERFORMANCE_SCREEN_NAME,
                          fundName: isin,
                          screenType: 'MF_L2'
                        }
                      })
                      isInteractionEventLocked.current = true
                    }
                    if (performanceHistoryRef && performanceHistoryRef?.current)
                      performanceHistoryRef.current.style.visibility = isDateToday ? 'visible' : 'hidden'
                  }}
                />
              )}
            </div>

            {isError ? (
              <div className='text-[12px] text-[#6A6E6E] font-[400] leading-[140%] pt-4 py-1 px-1'>
                {' '}
                Above Past Trends graph is unavailable due to some server issue. please try again after sometime{' '}
              </div>
            ) : (
              <div className='flex gap-3 pt-4 pb-2'>
                <div className='flex-1'>
                  <Tabs
                    isFullWidth
                    tabsData={tabsData}
                    onTabClick={(tabId: any) => {
                      trackEvent({
                        eventName: EventNames.GraphInteraction,
                        payload: {
                          eventId: 'graph_period',
                          responseName: `${tabId}`,
                          ScreenName: POWER_PERFORMANCE_SCREEN_NAME,
                          fundName: isin,
                          screenType: 'MF_L2'
                        }
                      })
                      setSelectedTabId(tabId)
                    }}
                    selectedTabId={selectedTabId}
                    tabsClassName='min-h-9'
                  />
                </div>
                {!isEliteButNoRanks && (
                  <div className='w-[120px]'>
                    <Button
                      isFullWidth
                      onClick={handleLabelsButtonClick}
                      buttonClassName='outline-none min-h-9'
                      aria-label={
                        showLabels
                          ? 'Power rank, selected, double tap to hide values in graph'
                          : 'Power rank, double tap to view values in graph'
                      }
                    >
                      <div aria-hidden='true' className='flex gap-2 font-[500] items-center justify-center'>
                        <div>
                          {showLabels ? (
                            <RenderHTML innerHTMLText={boltIcon} />
                          ) : (
                            <RenderHTML innerHTMLText={isUserElite ? crossBoltIcon : lockIcon} />
                          )}
                        </div>
                        <div className='text-[13px]'>Power Rank</div>
                      </div>
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {isFirstTimeElite ? (
            <div className='flex w-full pt-1'>
              <img src={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/hero-graph-elite-ftux.svg`} className='w-full' />
            </div>
          ) : null}
        </div>
      </div>
      <VaulDrawer open={openVaulBottomSheet} setOpen={setOpenVaulBottomSheet}>
        <PerformanceBottomSheet onDismiss={() => setOpenVaulBottomSheet((open) => !open)} ScreenName={''} />
      </VaulDrawer>
    </>
  )
}

const boltIcon = ` <svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'>
                          <path
                            d='M4.97358 12H3.95136L4.97358 7.33333H1.3958C0.496248 7.33333 1.05847 6.83333 1.07892 6.81333C2.39758 5.29333 4.38069 3.02667 7.02825 0H8.05047L7.02825 4.66667H10.6162C11.0251 4.66667 11.25 4.79333 11.0251 5.10667C6.98736 9.7 4.97358 12 4.97358 12Z'
                            fill='#232323'
                          />
                        </svg>`

const lockIcon = `  <svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'>
                          <path
                            d='M4.27266 3.58516V4.61875H7.71797V3.58516C7.71797 2.63339 6.94708 1.8625 5.99531 1.8625C5.04354 1.8625 4.27266 2.63339 4.27266 3.58516ZM2.89453 4.61875V3.58516C2.89453 1.87327 4.28342 0.484375 5.99531 0.484375C7.7072 0.484375 9.09609 1.87327 9.09609 3.58516V4.61875H9.44062C10.2007 4.61875 10.8187 5.23675 10.8187 5.99688V10.1313C10.8187 10.8914 10.2007 11.5094 9.44062 11.5094H2.55C1.78988 11.5094 1.17188 10.8914 1.17188 10.1313V5.99688C1.17188 5.23675 1.78988 4.61875 2.55 4.61875H2.89453Z'
                            fill='black'
                          />
                        </svg>`

const crossBoltIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
  <path d="M4.97358 13H3.95136L4.97358 8.33333H1.3958C0.496248 8.33333 1.05847 7.83333 1.07892 7.81333C2.39758 6.29333 4.38069 4.02667 7.02825 1H8.05047L7.02825 5.66667H10.6162C11.0251 5.66667 11.25 5.79333 11.0251 6.10667C6.98736 10.7 4.97358 13 4.97358 13Z" fill="#232323"/>
  <path d="M1.38477 2L9.85482 12.3832" stroke="#232323" stroke-width="1.5"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M11.0775 12.6762L0.923642 0.676183L1.84637 0.000427383L12.0014 12.0006L11.0775 12.6762Z" fill="#EBF2F2"/>
</svg>`
